/**
 * MGS custom JavaScript — CONNECT | 2u2 Web Technologies
 *
 * Warehouse exposes 'variant:changed', 'product:added' and 'cart:refresh' events on
 * document / documentElement; see the Warehouse theme documentation for the full list.
 * The modules below are MGS-specific.
 */

/**
 * MGS header cart badge — keeps the count showing distinct products (not total
 * quantity) in sync after AJAX cart actions (add, quantity change, remove).
 *
 * The initial page-load value is correct via Liquid (see sections/header-simple.liquid
 * and snippets/mini-cart.liquid, both render cart.items.size), but any cart action that
 * happens without a full page reload needs its own refresh, since Warehouse's own
 * quantity/remove controls update the cart over AJAX.
 */
(function () {
  function syncCartCountBadge() {
    var badge = document.querySelector('.header__cart-count');
    if (!badge) return;

    fetch(window.Shopify.routes.root + 'cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var distinctProductCount = cart.items.length;
        badge.textContent = distinctProductCount > 0 ? distinctProductCount : '';
      })
      .catch(function () {
        // Leave the badge as-is if the request fails; the next successful sync will correct it.
      });
  }

  document.addEventListener('product:added', syncCartCountBadge);
  document.addEventListener('cart:refresh', syncCartCountBadge);

  document.addEventListener('click', function (event) {
    if (event.target.closest('.quantity-selector, .mini-cart__quantity-remove')) {
      setTimeout(syncCartCountBadge, 600);
    }
  });
})();

/**
 * MGS accessibility — mini-cart icon open/closed state (v2.6).
 *
 * THE PROBLEM
 * sections/header-simple.liquid used to render
 *   <div class="header__cart-icon icon-state" aria-expanded="false">
 * inside the cart link. aria-expanded is not an allowed attribute on a plain <div>
 * with no role, which failed two audits at once: Lighthouse accessibility
 * ("[aria-*] attributes do not match their roles") and agentic browsing
 * ("Elements must only use supported ARIA attributes"). The correct owner of that
 * state is the <a class="header__cart-toggle">, which already carries aria-expanded
 * and aria-controls="mini-cart".
 *
 * WHY THE ATTRIBUTE COULD NOT SIMPLY BE DELETED
 * Warehouse swaps the cart glyph for a close glyph by keying its CSS off the element
 * that carries BOTH .icon-state and aria-expanded. Everywhere else in the header
 * those are the same element (the mobile hamburger button, for instance). The cart is
 * the one case where the toggle is an ANCESTOR of .icon-state — which is precisely
 * why the vendor duplicated the attribute onto the inner div. Remove it and nothing
 * tells the icon to change when the drawer opens.
 *
 * WHAT THIS DOES
 * Mirrors the state onto data-expanded, which assets/mgs-custom.css uses to drive the
 * same swap. It reads from whichever element actually holds aria-expanded, so it
 * works whether theme.js writes to the toggle, to the icon, or to both — and if
 * theme.js writes aria-expanded back onto the icon at runtime, the observer strips it
 * again after capturing the value, so the invalid attribute never survives in the DOM
 * that an auditor or an agent reads.
 */
(function () {
  function wire(icon) {
    var toggle = icon.closest('.header__cart-toggle') ||
                 icon.closest('[data-action="toggle-mini-cart"]');
    var expanded = false;

    function apply() {
      if (icon.hasAttribute('aria-expanded')) {
        // theme.js wrote to the icon: capture the value, then remove the attribute.
        expanded = icon.getAttribute('aria-expanded') === 'true';
        icon.removeAttribute('aria-expanded');
      } else if (toggle && toggle.hasAttribute('aria-expanded')) {
        expanded = toggle.getAttribute('aria-expanded') === 'true';
      }

      icon.setAttribute('data-expanded', expanded ? 'true' : 'false');
    }

    apply();

    if (typeof MutationObserver !== 'function') return;

    // Removing the attribute above re-enters apply(); that pass finds nothing to
    // remove, so it settles after one extra tick rather than looping.
    var observer = new MutationObserver(apply);
    observer.observe(icon, { attributes: true, attributeFilter: ['aria-expanded'] });
    if (toggle) {
      observer.observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
    }
  }

  function init() {
    var icons = document.querySelectorAll('.header__cart-icon');
    Array.prototype.forEach.call(icons, wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * MGS maximum order quantity — caps each material at MAX_PER_MATERIAL cubic metres.
 *
 * Why this exists: delivery is priced by truck load via ShipZip, using quantity tier
 * brackets that stop at 40 (five loads). ShipZip falls back to the rate's Base Price
 * when no tier matches, so a cart above the top bracket would be charged a single
 * load — a large and silent under-charge. Capping the cart at the same ceiling as the
 * tier table keeps that fallback unreachable in normal use.
 *
 * The cap is PER MATERIAL (per cart line), not per cart: a customer may still order
 * 40 m3 each of several different materials. That matches the delivery rule, where
 * materials are tipped separately and never share a load.
 *
 * To change the ceiling, edit MAX_PER_MATERIAL below and update the ShipZip tier
 * tables to match — the two numbers must always agree.
 */
(function () {
  var MAX_PER_MATERIAL = 40;
  var MESSAGE = 'Maximum ' + MAX_PER_MATERIAL + ' m³ per material online. For larger orders please call 1300 21 21 11 for a quote.';
  var cartClampAttempted = false;

  function quantityInputs() {
    return [].slice.call(document.querySelectorAll('input[name="quantity"], input[data-action="change-quantity"]'));
  }

  function applyMaxAttribute() {
    quantityInputs().forEach(function (input) {
      input.setAttribute('max', MAX_PER_MATERIAL);
    });
  }

  function showNotice(input) {
    var holder = input.closest('.product-form__quantity, .line-item__quantity, .quantity-selector');
    holder = holder || input.parentNode;
    if (!holder) return;

    var existing = holder.parentNode ? holder.parentNode.querySelector('[data-mgs-qty-notice]') : null;
    if (existing) return;

    var notice = document.createElement('p');
    notice.setAttribute('data-mgs-qty-notice', '');
    notice.textContent = MESSAGE;
    notice.style.margin = '8px 0 0';
    notice.style.fontSize = '0.85em';
    notice.style.color = 'var(--error-color, #cf0e0e)';

    if (holder.parentNode) {
      holder.parentNode.insertBefore(notice, holder.nextSibling);
      setTimeout(function () {
        if (notice.parentNode) notice.parentNode.removeChild(notice);
      }, 8000);
    }
  }

  function clampInput(input) {
    var value = parseInt(input.value, 10);
    if (isNaN(value)) return;
    if (value > MAX_PER_MATERIAL) {
      input.value = MAX_PER_MATERIAL;
      showNotice(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  document.addEventListener('input', function (event) {
    if (event.target && event.target.matches('input[name="quantity"], input[data-action="change-quantity"]')) {
      clampInput(event.target);
    }
  });

  document.addEventListener('change', function (event) {
    if (event.target && event.target.matches('input[name="quantity"], input[data-action="change-quantity"]')) {
      clampInput(event.target);
    }
  });

  // Backstop: if a cart line still exceeds the cap — a stale cart, a direct /cart/add
  // call, or the browser back button — correct it server-side once, then re-render.
  function clampCart() {
    if (cartClampAttempted) return;

    fetch(window.Shopify.routes.root + 'cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var offending = null;
        cart.items.forEach(function (item, index) {
          if (!offending && item.quantity > MAX_PER_MATERIAL) {
            offending = index + 1;
          }
        });
        if (!offending) return;

        cartClampAttempted = true;
        return fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: offending, quantity: MAX_PER_MATERIAL })
        }).then(function () {
          window.location.reload();
        });
      })
      .catch(function () {
        // Non-fatal: the ShipZip Base Price sentinel is the second line of defence.
      });
  }

  function init() {
    applyMaxAttribute();
    if (window.theme && window.theme.pageType === 'cart') {
      clampCart();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:refresh', applyMaxAttribute);
  document.addEventListener('product:added', applyMaxAttribute);
})();
